import classNames from 'classnames'
import React, { type CSSProperties, useMemo } from 'react'
import { icons } from '@opallabs/constellation'
import * as styles from './styles'
import { type IconProps, propDefaults } from './types'

export { type IconProps }
export { selectors as iconSelectors } from './styles'

interface AriaAnnotation {
  'aria-hidden'?: boolean
  'aria-label'?: string
  role?: string
}

export const Icon = React.memo(
  React.forwardRef<HTMLDivElement, IconProps>(
    (
      {
        ariaHidden,
        ariaLabel,
        ariaMode = propDefaults.ariaMode,
        className,
        color = propDefaults.color,
        glyph,
        height = propDefaults.height,
        htmlId,
        onClick,
        role = propDefaults.role,
        size,
        styleOverrides,
        tabIndex,
        useHypernova = propDefaults.useHypernova,
        width = propDefaults.width,
        ...otherProps
      },
      ref
    ): JSX.Element | null => {
      const SVG = useHypernova ? icons.hypernova[glyph] : icons.gem[glyph]

      const clickable = typeof onClick === 'function'

      const ariaAnnotation: AriaAnnotation = {
        role: clickable ? 'button' : role,
      }
      if (ariaHidden || ariaMode === 'hidden') {
        ariaAnnotation['aria-hidden'] = true
      } else if (ariaMode === 'presentational' && !clickable) {
        ariaAnnotation.role = 'presentation'
      }
      if (ariaLabel) {
        ariaAnnotation['aria-label'] = ariaLabel
      }

      const isSquare = !!size || height === width
      const nonSquareStyles = useMemo(
        () =>
          isSquare
            ? undefined
            : ({
                blockSize: height,
                inlineSize: width,
              } satisfies CSSProperties),
        [height, isSquare, width]
      )

      if (!SVG) return null

      return (
        // The following lint overrides are for the deprecated `onClick` prop.
        //
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className={classNames(styles.selectors.root, className, {
            [styles.stateVariables.clickable]: clickable,
            [styles.stateVariables.square]: isSquare,
          })}
          style={
            {
              [styles.cssVariables.color]: color,
              [styles.cssVariables.size]: isSquare
                ? `${size ?? height}px`
                : undefined,
            } as CSSProperties
          }
          css={[styles.root, nonSquareStyles, styleOverrides?.container]}
          id={htmlId}
          ref={ref}
          onClick={onClick}
          tabIndex={tabIndex}
          {...ariaAnnotation}
          {...otherProps}
        >
          <SVG
            css={[nonSquareStyles, styleOverrides?.icon]}
            className={styles.selectors.svg}
            aria-hidden
          />
        </div>
      )
    }
  )
)
Icon.displayName = 'Lapidary: Icon'
